import { useCallback } from 'react';
import { isSome } from 'fp-ts/Option';
import { useDashboard } from 'adapter/in/dashboards/context/Dashboard';
import Dashboard from 'domain/entity/dashboard';
import { UpdateDashboardNameCriteriaBuilder } from 'use-case/dashboard/update/updateDashboardCriteria/updateDashboardNameCriteria';
import { UpdateDashboardAccessCriteriaBuilder } from 'use-case/dashboard/update/updateDashboardCriteria/updateDashboardAccessCriteria';
import { useReportActionsDependencies } from 'adapter/in/reports/reportsDependencies';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { DASHBOARD_TYPE } from 'domain/entity/reportTemplate';

type UseUpdateDashboard = {
    update: (updatedDashboard: Dashboard) => void;
    isLoading: boolean;
    data: Dashboard | null;
    success: boolean;
    error: boolean;
    reset: () => void;
};

export const useUpdateNameDashboard = (): UseUpdateDashboard => {
    const { updateNameError, updateNameResult, updateNameIsLoading, updateName, updateNameReset } = useDashboard();
    const handleUpdate = useCallback(
        (updatedDashboard: Dashboard) =>
            updateName(UpdateDashboardNameCriteriaBuilder.create().withId(updatedDashboard.id).withName(updatedDashboard.name).build()),
        []
    );

    return {
        update: handleUpdate,
        isLoading: updateNameIsLoading,
        data: isSome(updateNameResult) ? updateNameResult.value : null,
        success: !!isSome(updateNameResult),
        error: !!isSome(updateNameError),
        reset: updateNameReset,
    };
};

export const useUpdateAccessDashboard = (type: DASHBOARD_TYPE = DASHBOARD_TYPE.DASHBOARD): UseUpdateDashboard => {
    const { updateAccessError, updateAccessResult, updateAccessIsLoading, updateAccess, updateAccessReset } = useDashboard();

    const { updateDashboardAccess } = useReportActionsDependencies();
    const {
        data: templateResult,
        execute: updateTemplateAccess,
        isLoading: templateIsLoading,
        error: templateError,
        reset: templateReset,
    } = useCase(updateDashboardAccess);

    const isTemplate = type === DASHBOARD_TYPE.REPORT_TEMPLATE;

    const handleUpdate = useCallback(
        (updatedDashboard: Dashboard) => {
            const criteria = UpdateDashboardAccessCriteriaBuilder.create()
                .withId(updatedDashboard.id)
                .withAccess(updatedDashboard.access)
                .build();

            if (isTemplate) {
                updateTemplateAccess(criteria);
                return;
            }

            updateAccess(criteria);
        },
        [isTemplate, updateAccess, updateTemplateAccess]
    );

    let isLoading: boolean;
    let data: Dashboard | null;
    let success: boolean;
    let error: boolean;
    let reset: () => void;

    if (isTemplate) {
        isLoading = templateIsLoading;
        data = isSome(templateResult) ? templateResult.value : null;
        success = !!isSome(templateResult);
        error = !!isSome(templateError);
        reset = templateReset;
    } else {
        isLoading = updateAccessIsLoading;
        data = isSome(updateAccessResult) ? updateAccessResult.value : null;
        success = !!isSome(updateAccessResult);
        error = !!isSome(updateAccessError);
        reset = updateAccessReset;
    }

    return {
        update: handleUpdate,
        isLoading,
        data,
        success,
        error,
        reset,
    };
};
